import { isTextFile } from './fileTypeHelpers';
import { generateTextThumbnail } from './textThumbnail';

/**
 * Generates a preview blob URL for a file (image or text).
 * Rejects if preview generation fails or file type is unsupported.
 *
 * @param fileObject - The file to generate a preview for
 * @returns Promise resolving to blob URL
 * @throws Error if file type is unsupported or generation fails
 */
export const IMAGE_EXTENSIONS: Set<string> = new Set([
	'png',
	'jpg',
	'jpeg',
	'gif',
	'webp',
	'svg',
	'bmp',
	'ico',
	'avif',
]);

/**
 * Returns true if the file is (or looks like) an image.
 * Falls back to checking the file extension when `file.type` is empty —
 * macOS screenshots and some clipboard pastes don't set MIME type.
 */
export function looksLikeImage(fileObject: File): boolean {
	if (fileObject.type.startsWith('image/')) {
		return true;
	}
	// Some browsers/OS don't set MIME on screenshots — fall back to extension.
	const ext = fileObject.name.split('.').pop()?.toLowerCase() ?? '';
	return IMAGE_EXTENSIONS.has(ext);
}

export async function generateFilePreview(fileObject: File): Promise<string> {
	if (looksLikeImage(fileObject)) {
		try {
			return URL.createObjectURL(fileObject);
		} catch (error) {
			const errorMessage =
				error instanceof Error
					? error.message
					: typeof error === 'string'
						? error
						: 'Failed to create blob URL for image file';
			throw new Error(`Failed to create blob URL for image: ${errorMessage}`);
		}
	} else if (isTextFile(fileObject.type, fileObject.name)) {
		return await generateTextThumbnail(fileObject);
	}
	throw new Error(`Unsupported file type: ${fileObject.type}`);
}
